import { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';

import { isAdminUser } from 'utils/userUtils';
import { actions } from 'data/store';
import { dataConfig } from 'data/dataConfig';
import {
	prettifyDataSource,
	getReducerName,
	COMBINED,
	BIS,
	OPEN_DATA,
} from 'data/dataSourceConstants';
import dataTables from 'view/dataTables';
import {
	getEntryKey,
	hasPrimarySourceDropdown,
	getPrimarySourcedMenuConfigForDataSet,
	getPrimarySourcedMenuLabel,
} from 'utils/primarySourcedDataSets';
import { fetchActiveDataSetTable } from 'utils/dataSetFetchUtils';
import { selectDataSet } from 'reducers/overviewDataSetReducer';
import {
	groupJobApplications,
	filterJobApplicationGroups,
	countJobApplicationGroups,
	countOpenJobApplications,
	paginateGroups,
} from 'utils/jobApplicationStatusUtils';
import DataTable from 'components/dataTable/DataTable';
import JobApplicationsGroupedTable from 'components/dataTable/JobApplicationsGroupedTable';
import DobNowJobApplicationsGroupedTable from 'components/dataTable/DobNowJobApplicationsGroupedTable';
import DataSourceSelect from 'components/dataTable/DataSourceSelect';
import FilterButtons from 'components/dataTable/FilterButtons';
import StatusFilterDropdown from 'components/dataTable/StatusFilterDropdown';
import OwnerFilterDropdown from 'components/dataTable/OwnerFilterDropdown';
import AcrisTypeFilterDropdown from 'components/dataTable/AcrisTypeFilterDropdown';
import PaginatorV3 from 'components/Paginator/PaginatorV3';
import Spinner from 'components/Spinner';
import OutboundLink from 'components/OutboundLink';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

import {
	groupDobNowJobApplications,
	filterDobNowJobApplicationGroups,
	paginateDobNowGroups,
	setDobNowClosedStatuses,
} from 'utils/dobNowJobApplicationStatusUtils';

import './DataTableContent.css';

const JOB_APPLICATIONS_PER_PAGE = 500;
const JOB_APPLICATION_GROUPS_PER_PAGE = 20;
// The API caps a page at 100 rows, so asking for more silently returns 100.
const DOB_NOW_APPLICATIONS_PER_PAGE = 100;
const DOB_NOW_GROUPS_PER_PAGE = 20;
const BIS_JOB_APPLICATIONS_REDUCER = getReducerName(
	dataConfig.jobApplications,
	BIS,
);
const OPEN_DATA_JOB_APPLICATIONS_REDUCER = getReducerName(
	dataConfig.jobApplications,
	OPEN_DATA,
);

function fetchJobApplicationsForSource(
	dispatch,
	propertyId,
	source,
	searchString,
) {
	const reducerName = getReducerName(dataConfig.jobApplications, source);
	// fetchAllPages, not fetchData: the grouped table decides each job's status from
	// its own filings, so a single capped page (backend clamps per_page to 100) can
	// silently drop most of a property's jobs and misreport the Open/Resolved counts.
	// See genericDataSetReducer.js's fetchAllPages doc comment.
	return dispatch(
		actions[reducerName].fetchAllPages(
			propertyId,
			'all',
			searchString,
			{},
			JOB_APPLICATIONS_PER_PAGE,
		),
	);
}

function DataTableContent(props) {
	const dispatch = useDispatch();
	const {
		dataSet,
		reducerName,
		dataSource,
		title,
		columns,
		tableView,
		onSwitchSource,
	} = props;
	let {
		propertyId,
		isLoading,
		data,
		dataSetLastModified,
		currentPage,
		totalPages,
		filteredStatus,
		sortField,
		sortDirection,
		numOpen,
		numClosed,
		numCombined,
		closedStatuses,
		dataSetUrl,
		reduxSearchString,
		dataSetStatuses,
		propertyData,
		user,
		emptyStateMessage,
		bisJobApplicationsData,
		openDataJobApplicationsData,
	} = useSelector((state) => ({
		propertyId: state.property.propertyData?.id,
		isLoading:
			state[reducerName].isLoading ||
			state.property.isLoading ||
			state.address.isLoading,
		data: state[reducerName].data,
		dataSetLastModified: state[reducerName].dataSetLastModified,
		currentPage: state[reducerName].currentPage,
		totalPages: state[reducerName].totalPages,
		filteredStatus: state[reducerName].filteredStatus,
		sortField: state[reducerName].sortField,
		sortDirection: state[reducerName].sortDirection,
		numOpen: state[reducerName].numOpen,
		numClosed: state[reducerName].numClosed,
		numCombined: state[reducerName]?.numCombined || 0,
		closedStatuses: state[reducerName]?.closedStatuses || [],
		dataSetUrl: state[reducerName].dataSetUrl,
		emptyStateMessage: state[reducerName].emptyStateMessage,
		reduxSearchString: state[reducerName].searchString || '',
		dataSetStatuses: state[reducerName].statuses || [],
		propertyData: state.property.propertyData,
		user: state.user.userData,
		bisJobApplicationsData: state[BIS_JOB_APPLICATIONS_REDUCER]?.data || [],
		openDataJobApplicationsData:
			state[OPEN_DATA_JOB_APPLICATIONS_REDUCER]?.data || [],
	}));
	let [searchString, setSearchString] = useState('');
	let [ownerFilter, setOwnerFilter] = useState('all');
	let [jobAppGroupPage, setJobAppGroupPage] = useState(1);
	let [jobAppFilteredStatus, setJobAppFilteredStatus] = useState('open');
	let [dobNowGroupPage, setDobNowGroupPage] = useState(1);
	let [dobNowFilteredStatus, setDobNowFilteredStatus] = useState('open');

	const isAdmin = isAdminUser(user);
	const isJobApplications = dataSet === 'jobApplications';
	const isLegacyJobApplications =
		isJobApplications && tableView === 'legacy' && isAdmin;
	const isGroupedJobApplications =
		isJobApplications && !isLegacyJobApplications;
	const isDobJobApplications = dataSet === 'dobJobApplications';
	const isFdnyPermits2 = dataSet === 'fdnyPermits2';
	const isAcrisDocuments = dataSet === 'acrisDocuments';

	// Track the selected source for the DOB Complaints Resources dropdown
	// 'combined' = All, 'bis' = BIS only, 'open_data' = 311 and BIS
	let [dobComplaintsSource, setDobComplaintsSource] = useState('combined');
	let [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
	const resourcesDropdownRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				resourcesDropdownRef.current &&
				!resourcesDropdownRef.current.contains(event.target)
			) {
				setResourcesDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const dobComplaintsSourceOptions = [
		{
			value: 'combined',
			label: 'All',
			dataSet: 'dobComplaintsCombined',
			dataSource: COMBINED,
		},
		{ value: 'bis', label: 'BIS', dataSet: 'dobComplaints', dataSource: BIS },
		{
			value: 'open_data',
			label: '311 and BIS',
			dataSet: 'dobComplaints',
			dataSource: OPEN_DATA,
		},
	];

	const handleDobComplaintsSourceChange = (value) => {
		setDobComplaintsSource(value);
		setResourcesDropdownOpen(false);
		const option = dobComplaintsSourceOptions.find((o) => o.value === value);
		if (option && onSwitchSource) {
			onSwitchSource(option.dataSet, option.dataSource);
		}
	};

	// Sync local dropdown state when dataSet changes externally
	// (e.g. user navigates away and back)
	useEffect(() => {
		if (dataSet === 'dobComplaintsCombined') {
			setDobComplaintsSource('combined');
		} else if (dataSet === 'dobComplaints' && dataSource === BIS) {
			setDobComplaintsSource('bis');
		} else if (dataSet === 'dobComplaints' && dataSource === OPEN_DATA) {
			setDobComplaintsSource('open_data');
		}
	}, [dataSet, dataSource]);

	// ECB Violations source
	const [ecbViolationsSource, setEcbViolationsSource] = useState('combined');
	const ecbViolationsSourceOptions = [
		{ value: 'combined', label: 'All', dataSet: 'ecbViolationsCombined', dataSource: COMBINED },
		{ value: 'bis', label: 'BIS', dataSet: 'ecbViolations', dataSource: BIS },
		{ value: 'open_data', label: 'DOB NOW', dataSet: 'ecbViolations', dataSource: OPEN_DATA },
	];

	const handleEcbViolationsSourceChange = (value) => {
		setEcbViolationsSource(value);
		setResourcesDropdownOpen(false);
		const option = ecbViolationsSourceOptions.find(o => o.value === value);
		if (option && onSwitchSource) {
			onSwitchSource(option.dataSet, option.dataSource);
		}
	};

	// OATH/ECB Hearings source. The ticket names the two sources "OATH/ECB"
	// (the ECB Ticket Finder scrape) and "Source 2" (the Open Data feed).
	const [oathHearingsSource, setOathHearingsSource] = useState('combined');
	const oathHearingsSourceOptions = [
		{ value: 'combined', label: 'All', dataSet: 'oathHearingsCombined', dataSource: COMBINED },
		{ value: 'ecb', label: 'OATH/ECB', dataSet: 'ecbTicketFinderHearings', dataSource: OPEN_DATA },
		{ value: 'open_data', label: 'Source 2', dataSet: 'fdnyOathHearings', dataSource: OPEN_DATA },
	];

	const handleOathHearingsSourceChange = (value) => {
		setOathHearingsSource(value);
		setResourcesDropdownOpen(false);
		const option = oathHearingsSourceOptions.find(o => o.value === value);
		if (option && onSwitchSource) {
			onSwitchSource(option.dataSet, option.dataSource);
		}
	};

	// Track the selected source for the DOB Violations Resources dropdown
	// 'combined' = All, 'bis' = BIS only, 'open_data' = BIS 2
	let [dobViolationsSource, setDobViolationsSource] = useState('combined');

	const dobViolationsSourceOptions = [
		{ value: 'combined', label: 'All', dataSet: 'dobViolationsCombined', dataSource: COMBINED },
		{ value: 'bis', label: 'BIS', dataSet: 'dobViolations', dataSource: BIS },
		{ value: 'open_data', label: 'BIS 2', dataSet: 'dobViolations', dataSource: OPEN_DATA },
	];

	const handleDobViolationsSourceChange = (value) => {
		setDobViolationsSource(value);
		setResourcesDropdownOpen(false);
		const option = dobViolationsSourceOptions.find(o => o.value === value);
		if (option && onSwitchSource) {
			onSwitchSource(option.dataSet, option.dataSource);
		}
	};

	// Sync local dropdown states when dataSet / dataSource changes externally
	useEffect(() => {
		if (dataSet === 'ecbViolationsCombined') {
			setEcbViolationsSource('combined');
		} else if (dataSet === 'ecbViolations' && dataSource === BIS) {
			setEcbViolationsSource('bis');
		} else if (dataSet === 'ecbViolations' && dataSource === OPEN_DATA) {
			setEcbViolationsSource('open_data');
		}
	}, [dataSet, dataSource]);

	useEffect(() => {
		if (dataSet === 'oathHearingsCombined') {
			setOathHearingsSource('combined');
		} else if (dataSet === 'ecbTicketFinderHearings') {
			setOathHearingsSource('ecb');
		} else if (dataSet === 'fdnyOathHearings') {
			setOathHearingsSource('open_data');
		}
	}, [dataSet, dataSource]);

	useEffect(() => {
		if (dataSet === 'dobViolationsCombined') {
			setDobViolationsSource('combined');
		} else if (dataSet === 'dobViolations' && dataSource === BIS) {
			setDobViolationsSource('bis');
		} else if (dataSet === 'dobViolations' && dataSource === OPEN_DATA) {
			setDobViolationsSource('open_data');
		}
	}, [dataSet, dataSource]);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (!propertyId) return;

		setSearchString(reduxSearchString);

		if (isGroupedJobApplications) {
			fetchJobApplicationsForSource(
				dispatch,
				propertyId,
				BIS,
				reduxSearchString,
			);
			fetchJobApplicationsForSource(
				dispatch,
				propertyId,
				OPEN_DATA,
				reduxSearchString,
			);
			return;
		}

		if (isDobJobApplications) {
			dispatch(
				actions[reducerName].fetchAllPages(
					propertyId,
					'all',
					reduxSearchString,
					{},
					DOB_NOW_APPLICATIONS_PER_PAGE,
				),
			);
			return;
		}

		if (isLegacyJobApplications) {
			dispatch(
				actions[reducerName].fetchData(propertyId, 'all', 1, reduxSearchString),
			);
			return;
		}

		fetchActiveDataSetTable(dispatch, actions, {
			propertyId,
			dataSet,
			reducerName,
			filteredStatus,
			searchString: reduxSearchString,
		});
	}, [propertyId, reducerName, dataSet, reduxSearchString, tableView]);

	useEffect(() => {
		setSearchString(reduxSearchString);
	}, [reduxSearchString]);

	useEffect(() => {
		setJobAppGroupPage(1);
	}, [dataSet, dataSource, jobAppFilteredStatus, searchString, data]);

	useEffect(() => {
		setDobNowGroupPage(1);
	}, [dataSet, dobNowFilteredStatus, searchString, data]);

	let subtitle = `(${prettifyDataSource(dataSource)})`;
	let showStatusFilter =
		dataConfig[dataSet].hasStatus !== false && !isFdnyPermits2;
	const showPrimarySourceDropdown =
		hasPrimarySourceDropdown(dataSet) && !isLegacyJobApplications;
	const menuConfig = getPrimarySourcedMenuConfigForDataSet(dataSet);
	const displayTitle = menuConfig
		? getPrimarySourcedMenuLabel(menuConfig)
		: title;
	const openLabel = isFdnyPermits2 ? 'Expired' : (dataSet === 'bisIlluminatedSigns' ? 'Expired' : 'Open');
	//dotLiens closes by the clerk recording a release, so 'Released' is the accurate label
	const closedLabel = isFdnyPermits2 ? 'Active' : (dataSet === 'bisIlluminatedSigns' ? 'Active' : (dataSet === 'dotLiens' ? 'Released' : 'Resolved'));
	const statusForFetch = isFdnyPermits2 ? 'all' : filteredStatus;

	const acrisTypeOptions = useMemo(() => {
		if (!isAcrisDocuments) {
			return [];
		}
		const fromApi = Array.isArray(dataSetStatuses) ? dataSetStatuses : [];
		const fromRows = (Array.isArray(data) ? data : [])
			.map((row) => row?.record_type || row?.doc_type)
			.filter(Boolean);
		return [...fromApi, ...fromRows];
	}, [isAcrisDocuments, dataSetStatuses, data]);

	const onSourceChange = (nextDataSet, nextSource) => {
		dispatch(
			selectDataSet({
				dataSet: nextDataSet,
				reducerName: getReducerName(dataConfig[nextDataSet], nextSource),
				dataSource: nextSource,
				title: dataTables[nextDataSet].title,
				columns: dataTables[nextDataSet][nextSource].columns,
				tableView: 'grouped',
			}),
		);
	};

	const jobApplicationSourceCounts = useMemo(() => {
		if (!isGroupedJobApplications) {
			return {};
		}
		return {
			[getEntryKey('jobApplications', BIS)]: countOpenJobApplications(
				bisJobApplicationsData,
				BIS,
			),
			[getEntryKey('jobApplications', OPEN_DATA)]: countOpenJobApplications(
				openDataJobApplicationsData,
				OPEN_DATA,
			),
		};
	}, [
		isGroupedJobApplications,
		bisJobApplicationsData,
		openDataJobApplicationsData,
	]);

	const jobApplicationGroups = useMemo(() => {
		if (!isGroupedJobApplications || !Array.isArray(data)) {
			return [];
		}
		return groupJobApplications(data, dataSource);
	}, [data, dataSource, isGroupedJobApplications]);

	const filteredJobApplicationGroups = useMemo(() => {
		if (!isGroupedJobApplications) {
			return [];
		}
		return filterJobApplicationGroups(
			jobApplicationGroups,
			jobAppFilteredStatus,
		);
	}, [isGroupedJobApplications, jobApplicationGroups, jobAppFilteredStatus]);

	const jobApplicationCounts = useMemo(() => {
		if (!isGroupedJobApplications) {
			return { numOpen, numClosed, numCombined };
		}
		return countJobApplicationGroups(jobApplicationGroups);
	}, [
		isGroupedJobApplications,
		jobApplicationGroups,
		numOpen,
		numClosed,
		numCombined,
	]);

	const paginatedJobApplicationGroups = useMemo(() => {
		if (!isGroupedJobApplications) {
			return { groups: [], currentPage: 1, totalPages: 1 };
		}
		return paginateGroups(
			filteredJobApplicationGroups,
			jobAppGroupPage,
			JOB_APPLICATION_GROUPS_PER_PAGE,
		);
	}, [isGroupedJobApplications, filteredJobApplicationGroups, jobAppGroupPage]);

	const dobNowGroups = useMemo(() => {
		if (!isDobJobApplications || !Array.isArray(data)) {
			return [];
		}
		// Adopt the server's closed list before grouping. Grouping decides each job's
		// open or resolved state, so it has to run against the same labels the server
		// used for the tab counts, or the tabs and the rows describe different sets.
		setDobNowClosedStatuses(closedStatuses);
		return groupDobNowJobApplications(data);
	}, [data, isDobJobApplications, closedStatuses]);

	const filteredDobNowGroups = useMemo(() => {
		if (!isDobJobApplications) {
			return [];
		}
		return filterDobNowJobApplicationGroups(dobNowGroups, dobNowFilteredStatus);
	}, [isDobJobApplications, dobNowGroups, dobNowFilteredStatus]);

	const paginatedDobNowGroups = useMemo(() => {
		if (!isDobJobApplications) {
			return { groups: [], currentPage: 1, totalPages: 1 };
		}
		return paginateDobNowGroups(
			filteredDobNowGroups,
			dobNowGroupPage,
			DOB_NOW_GROUPS_PER_PAGE,
		);
	}, [isDobJobApplications, filteredDobNowGroups, dobNowGroupPage]);

	const ownerOptions = useMemo(() => {
		if (!isFdnyPermits2 || !Array.isArray(data)) {
			return [];
		}
		const normalizedToLabel = new Map();
		data.forEach((row) => {
			const ownerName = (row?.owner_name || '').trim();
			if (!ownerName) return;
			const normalized = ownerName.toLowerCase();
			if (!normalizedToLabel.has(normalized)) {
				normalizedToLabel.set(normalized, ownerName);
			}
		});
		return Array.from(normalizedToLabel.values()).sort((a, b) =>
			a.localeCompare(b),
		);
	}, [data, isFdnyPermits2]);

	useEffect(() => {
		if (!isFdnyPermits2) return;
		if (ownerFilter === 'all') return;
		if (!ownerOptions.includes(ownerFilter)) {
			setOwnerFilter('all');
		}
	}, [isFdnyPermits2, ownerFilter, ownerOptions]);

	const filteredData = useMemo(() => {
		if (!isFdnyPermits2 || ownerFilter === 'all') {
			return data;
		}
		if (!Array.isArray(data)) {
			return data;
		}
		const target = ownerFilter.trim().toLowerCase();
		return data.filter((row) => {
			const ownerName = (row?.owner_name || '').trim().toLowerCase();
			return ownerName === target;
		});
	}, [data, isFdnyPermits2, ownerFilter]);

	const debouncedSearch = useMemo(
		() =>
			debounce((searchValue) => {
				if (isGroupedJobApplications) {
					fetchJobApplicationsForSource(dispatch, propertyId, BIS, searchValue);
					fetchJobApplicationsForSource(
						dispatch,
						propertyId,
						OPEN_DATA,
						searchValue,
					);
					return;
				}
				if (isDobJobApplications) {
					dispatch(
						actions[reducerName].fetchAllPages(
							propertyId,
							'all',
							searchValue,
							{},
							DOB_NOW_APPLICATIONS_PER_PAGE,
						),
					);
					return;
				}
				if (isLegacyJobApplications) {
					dispatch(
						actions[reducerName].fetchData(propertyId, 'all', 1, searchValue),
					);
					return;
				}
				dispatch(
					actions[reducerName].fetchData(
						propertyId,
						statusForFetch,
						1,
						searchValue,
					),
				);
			}, 400),
		[
			dispatch,
			reducerName,
			propertyId,
			statusForFetch,
			isGroupedJobApplications,
			isLegacyJobApplications,
			isDobJobApplications,
		],
	);

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchString(value);
		debouncedSearch(value);
	};

	const openInModal = () => {
		let options = {
			dataSet,
			reducerName,
			dataSource,
			title,
			columns,
			filteredStatus: isGroupedJobApplications
				? jobAppFilteredStatus
				: isDobJobApplications
					? dobNowFilteredStatus
					: filteredStatus,
			tableView,
		};
		dispatch(openModal(MODAL_TYPES.DATA_TABLE, options));
	};

	const onSort = (nextSort) => {
		if (isGroupedJobApplications) {
			const activeReducer = getReducerName(
				dataConfig.jobApplications,
				dataSource,
			);
			return dispatch(
				actions[activeReducer].fetchAllPages(
					propertyId,
					'all',
					searchString,
					nextSort,
					JOB_APPLICATIONS_PER_PAGE,
				),
			);
		}
		if (isLegacyJobApplications) {
			return dispatch(
				actions[reducerName].fetchData(
					propertyId,
					'all',
					1,
					searchString,
					nextSort,
				),
			);
		}
		if (isDobJobApplications) {
			return dispatch(
				actions[reducerName].fetchAllPages(
					propertyId,
					'all',
					searchString,
					nextSort,
					DOB_NOW_APPLICATIONS_PER_PAGE,
				),
			);
		}
		return dispatch(
			actions[reducerName].fetchData(
				propertyId,
				statusForFetch,
				1,
				searchString,
				nextSort,
			),
		);
	};

	let modifiedDate = null;
	let modifiedDateString = '';
	if (dataSetLastModified) {
		modifiedDate = new Date(0);
		modifiedDate.setUTCSeconds(dataSetLastModified);
		let [month, day, year] = [
			modifiedDate.getMonth() + 1,
			modifiedDate.getDate(),
			modifiedDate.getFullYear(),
		];
		modifiedDateString = `${month}/${day}/${year}`;
	}

	return (
		<div className="rd-overview-data-table-content">
			<div className={`data-table-container`}>
				<nav className="data-table-card-header">
					<div className="data-table-card-header-row">
						<div className="data-table-card-title-block">
							<div className="d-flex align-items-center flex-wrap">
								<span className="data-set-name">{displayTitle}</span>
								{showPrimarySourceDropdown && (
									<DataSourceSelect
										dataSet={dataSet}
										dataSource={dataSource}
										propertyData={propertyData}
										sourceCounts={jobApplicationSourceCounts}
										onSourceChange={onSourceChange}
										isDisabled={isLoading}
									/>
								)}
								{/* Resources dropdown – ECB Violations combined source switcher */}
								{(dataSet === 'ecbViolationsCombined' || dataSet === 'ecbViolations') && onSwitchSource && (
									<div className="status-filter-dropdown d-inline-block ms-2 align-middle" ref={resourcesDropdownRef}>
										<button
											disabled={isLoading}
											className={`filter-button btn btn-sm ${ecbViolationsSource === 'combined' ? 'btn-success' : 'btn-primary'} dropdown-toggle px-2`}
											onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
										>
											Resources: {ecbViolationsSourceOptions.find(o => o.value === ecbViolationsSource)?.label || 'All'}
											<span className="caret"></span>
										</button>
										{resourcesDropdownOpen && (
											<div className="status-filter-menu" style={{ left: 0, right: 'auto' }}>
												{ecbViolationsSourceOptions.map((opt) => (
													<div
														key={opt.value}
														className={`status-filter-item ${ecbViolationsSource === opt.value ? 'active' : ''}`}
														onClick={() => handleEcbViolationsSourceChange(opt.value)}
													>
														{opt.label}
													</div>
												))}
											</div>
										)}
									</div>
								)}
								{(dataSet === 'dobComplaintsCombined' ||
									dataSet === 'dobComplaints') &&
									onSwitchSource && (
										<div
											className="status-filter-dropdown d-inline-block ms-2 align-middle"
											ref={resourcesDropdownRef}
										>
											<button
												disabled={isLoading}
												className={`filter-button btn btn-sm ${dobComplaintsSource === 'combined' ? 'btn-success' : 'btn-primary'} dropdown-toggle px-2`}
												onClick={() =>
													setResourcesDropdownOpen(!resourcesDropdownOpen)
												}
											>
												Resources:{' '}
												{dobComplaintsSourceOptions.find(
													(o) => o.value === dobComplaintsSource,
												)?.label || 'All'}
												<span className="caret"></span>
											</button>
											{resourcesDropdownOpen && (
												<div
													className="status-filter-menu"
													style={{ left: 0, right: 'auto' }}
												>
													{dobComplaintsSourceOptions.map((opt) => (
														<div
															key={opt.value}
															className={`status-filter-item ${dobComplaintsSource === opt.value ? 'active' : ''}`}
															onClick={() =>
																handleDobComplaintsSourceChange(opt.value)
															}
														>
															{opt.label}
														</div>
													))}
												</div>
											)}
										</div>
									)}
								{/* Resources dropdown – OATH/ECB Hearings combined source switcher */}
								{(dataSet === 'oathHearingsCombined' || dataSet === 'ecbTicketFinderHearings' || dataSet === 'fdnyOathHearings') && onSwitchSource && (
									<div className="status-filter-dropdown d-inline-block ms-2 align-middle" ref={resourcesDropdownRef}>
										<button
											disabled={isLoading}
											className={`filter-button btn btn-sm ${oathHearingsSource === 'combined' ? 'btn-success' : 'btn-primary'} dropdown-toggle px-2`}
											onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
										>
											Resources: {oathHearingsSourceOptions.find(o => o.value === oathHearingsSource)?.label || 'All'}
											<span className="caret"></span>
										</button>
										{resourcesDropdownOpen && (
											<div className="status-filter-menu" style={{ left: 0, right: 'auto' }}>
												{oathHearingsSourceOptions.map((opt) => (
													<div
														key={opt.value}
														className={`status-filter-item ${oathHearingsSource === opt.value ? 'active' : ''}`}
														onClick={() => handleOathHearingsSourceChange(opt.value)}
													>
														{opt.label}
													</div>
												))}
											</div>
										)}
									</div>
								)}
								{/* Resources dropdown – DOB Violations combined source switcher */}
								{(dataSet === 'dobViolationsCombined' || dataSet === 'dobViolations') && onSwitchSource && (
									<div className="status-filter-dropdown d-inline-block ms-2 align-middle" ref={resourcesDropdownRef}>
										<button
											disabled={isLoading}
											className={`filter-button btn btn-sm ${dobViolationsSource === 'combined' ? 'btn-success' : 'btn-primary'} dropdown-toggle px-2`}
											onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
										>
											Resources: {dobViolationsSourceOptions.find(o => o.value === dobViolationsSource)?.label || 'All'}
											<span className="caret"></span>
										</button>
										{resourcesDropdownOpen && (
											<div className="status-filter-menu" style={{ left: 0, right: 'auto' }}>
												{dobViolationsSourceOptions.map((opt) => (
													<div
														key={opt.value}
														className={`status-filter-item ${dobViolationsSource === opt.value ? 'active' : ''}`}
														onClick={() => handleDobViolationsSourceChange(opt.value)}
													>
														{opt.label}
													</div>
												))}
											</div>
										)}
									</div>
								)}
								{isAdmin && !showPrimarySourceDropdown && (
									<span className="navbar-text data-source-name">
										<OutboundLink url={dataSetUrl}>{subtitle}</OutboundLink>
									</span>
								)}
								{isLegacyJobApplications && (
									<span className="navbar-text data-source-name ms-2">
										(Legacy)
									</span>
								)}
							</div>
							{dataSetLastModified && (
								<div className="data-set-last-modified">
									Last Modified: {modifiedDateString}
								</div>
							)}
						</div>
						<div className="data-table-search-wrapper">
							<SearchIcon className="data-table-search-icon" aria-hidden="true" />
							<input
								type="text"
								className="form-control data-table-search"
								placeholder="Search"
								aria-label={`Search ${title}`}
								value={searchString}
								onChange={handleSearchChange}
							/>
						</div>
						<div className="table-controls">
							{isFdnyPermits2 && (
								<OwnerFilterDropdown
									onFilter={(owner) => setOwnerFilter(owner)}
									filteredOwner={ownerFilter}
									owners={ownerOptions}
									isDisabled={isLoading}
								/>
							)}
							{isAcrisDocuments && (
								<AcrisTypeFilterDropdown
									onFilter={(type) =>
										dispatch(
											actions[reducerName].fetchData(
												propertyId,
												type === 'all' ? null : type,
												1,
												reduxSearchString
											)
										)
									}
									filteredType={filteredStatus || 'all'}
									types={acrisTypeOptions}
									isDisabled={isLoading}
								/>
							)}
							{showStatusFilter &&
								!isGroupedJobApplications &&
								!isDobJobApplications && (
									<FilterButtons
										onFilter={(status) =>
											dispatch(
												actions[reducerName].fetchData(
													propertyId,
													status,
													1,
													reduxSearchString,
												),
											)
										}
										filteredStatus={filteredStatus}
										numOpen={numOpen}
										numClosed={numClosed}
										numCombined={numCombined}
										openLabel={openLabel}
										closedLabel={closedLabel}
										isDisabled={isLoading}
									/>
								)}
							{isGroupedJobApplications && (
								<FilterButtons
									onFilter={(status) => setJobAppFilteredStatus(status)}
									filteredStatus={jobAppFilteredStatus}
									numOpen={jobApplicationCounts.numOpen}
									numClosed={jobApplicationCounts.numClosed}
									numCombined={jobApplicationCounts.numCombined}
									openLabel="Open"
									closedLabel="Resolved"
									isDisabled={isLoading}
								/>
							)}
							{isDobJobApplications && (
								/*
								 * Counts come from the API, which computes them over every filing
								 * for the property. Deriving them from `data` would only ever count
								 * the first page of rows the API is willing to return.
								 */
								<FilterButtons
									onFilter={(status) => setDobNowFilteredStatus(status)}
									filteredStatus={dobNowFilteredStatus}
									numOpen={numOpen}
									numClosed={numClosed}
									numCombined={numCombined}
									openLabel={openLabel}
									closedLabel={closedLabel}
									isDisabled={isLoading}
								/>
							)}
							{isLegacyJobApplications && (
								<StatusFilterDropdown
									onFilter={(status) =>
										dispatch(
											actions[reducerName].fetchData(
												propertyId,
												status,
												1,
												searchString,
											),
										)
									}
									filteredStatus={filteredStatus}
									statuses={dataSetStatuses}
									isDisabled={isLoading}
								/>
							)}
							<button
								className="btn btn-hyperlink ms-3"
								onClick={() => openInModal()}
							>
								<i className="bi bi-fullscreen" />
							</button>
						</div>
					</div>
				</nav>
				{isLoading ? (
					<Spinner />
				) : (
					<div className="table-paginator-wrapper">
						{data?.length === 0 && emptyStateMessage && (
							<div className="alert alert-info" role="alert">
								{emptyStateMessage}
							</div>
						)}
						<div className="table-wrapper">
							{isGroupedJobApplications ? (
								<JobApplicationsGroupedTable
									columns={columns}
									groups={paginatedJobApplicationGroups.groups}
									dataSet={dataSet}
									dataSource={dataSource}
									propertyData={propertyData}
									isLoading={isLoading}
									searchString={searchString}
									sortField={sortField}
									sortDirection={sortDirection}
									onSort={onSort}
								/>
							) : isDobJobApplications ? (
								<DobNowJobApplicationsGroupedTable
									columns={columns}
									groups={paginatedDobNowGroups.groups}
									dataSet={dataSet}
									dataSource={dataSource}
									propertyData={propertyData}
									isLoading={isLoading}
									searchString={searchString}
									sortField={sortField}
									sortDirection={sortDirection}
									onSort={onSort}
								/>
							) : (
								<DataTable
									columns={columns}
									data={filteredData}
									dataSet={dataSet}
									dataSource={dataSource}
									propertyData={propertyData}
									isLoading={isLoading}
									searchString={searchString}
									sortField={sortField}
									sortDirection={sortDirection}
									onSort={onSort}
								/>
							)}
						</div>
						{/* violerts-v3-root gives PaginatorV3 its button reset, same as AlertsTable */}
						<div className="data-table-paginator violerts-v3-root tw-flex tw-justify-center tw-pt-4">
							<PaginatorV3
								currentPage={
									(isGroupedJobApplications
										? paginatedJobApplicationGroups.currentPage
										: isDobJobApplications
											? paginatedDobNowGroups.currentPage
											: currentPage) || 1
								}
								totalPages={
									(isGroupedJobApplications
										? paginatedJobApplicationGroups.totalPages
										: isDobJobApplications
											? paginatedDobNowGroups.totalPages
											: totalPages) || 0
								}
								onPageChange={(pageNum) => {
									if (isGroupedJobApplications) {
										setJobAppGroupPage(pageNum);
									} else if (isDobJobApplications) {
										setDobNowGroupPage(pageNum);
									} else {
										dispatch(actions[reducerName].setPage(pageNum));
									}
								}}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default DataTableContent;
